// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import type { ITheme } from "@fluentui/react";
// eslint-disable-next-line @1js/office-online-rules/no-v8-imports-for-fluentui -- disabling for existing usage
import { memoizeFunction, mergeStyles, mergeStyleSets } from "@fluentui/react";
import type { IVerticalDividerClassNames } from "@fluentui/react/lib/Divider";

import { isThemingEnabled } from "@1js/acui-theme";

import { scalePropertyWithDpi } from "./borderScaling";
import type { AppColors } from "../utilities/appColors";
import type {
  IContextualMenuClassNames,
  IMenuItemClassNames,
} from "../utilities/fluentui-react-type-polyfills";
import {
  baseFabricFocusSelector,
  baseFabricHoverFocusSelector,
  baseFabricHoverSelector,
  getContextualMenuStyles,
  getMenuItemStyles,
} from "./commonStyles";
import { getStandardControlSemanticColorsWithTheme } from "./standardControlSemanticColors";
import { ribbonDividerWrapperClassName } from "./classnameConstants";

// This is an acceptable way of breaking a line in multiple sentences
// without impacting the accessibility tree so that scan mode reads
// the sentence as a unit and not separate lines.
// Visually, it's equivalent to having a `<br>` without the impact this has on scan mode.
export const pseudoTextLineBreakClassNames = mergeStyles({
  selectors: {
    ":before": {
      whiteSpace: "pre", // breaks lines at new line characters
      content: '"\\a"', // inserts a new line character
    },
  },
});

export const getContextualMenuClassNames = memoizeFunction(
  (
    theme: ITheme,
    appColors?: AppColors,
    className?: string
  ): IContextualMenuClassNames => {
    const styles = getContextualMenuStyles(theme, appColors);

    // Fabric issue #6922: there's a bug with concatStyleSets and mergeStyles that
    // gives the wrong type for IContextualMenuClassName
    return {
      ...mergeStyleSets({
        container: [
          styles.container,
          className,
          [
            {
              selectors: {
                ":focus": { outline: 0 },
              },
            },
          ],
        ],
        root: styles.root,
        list: styles.list,
        header: styles.header,
        title: styles.title,
      }),
      subComponentStyles: styles.subComponentStyles,
    };
  }
);

export const getItemClassNames = memoizeFunction(
  (
    theme: ITheme,
    appColors: AppColors,
    disabled: boolean,
    expanded: boolean,
    checked: boolean,
    isAnchorLink: boolean,
    knownIcon: boolean,
    itemClassName?: string,
    dividerClassName?: string,
    iconClassName?: string,
    subMenuClassName?: string
  ): IMenuItemClassNames => {
    const styles = getMenuItemStyles(theme, appColors, disabled);

    return mergeStyleSets({
      item: [styles.item, itemClassName],
      divider: [styles.divider, dividerClassName],
      root: [
        styles.root,
        checked && styles.rootChecked,
        isAnchorLink && styles.anchorLink,
        expanded && styles.rootExpanded,
        disabled && styles.rootDisabled,
        !disabled &&
          !expanded && [
            {
              selectors: {
                ":hover": styles.rootHovered,
                ":active": styles.rootPressed,
                [`${baseFabricFocusSelector}, ${baseFabricHoverFocusSelector}`]:
                  styles.rootFocused,
                [baseFabricHoverSelector]: { background: "inherit;" },
              },
            },
          ],
      ],
      splitPrimary: [
        styles.root,
        checked && styles.rootChecked,
        disabled && styles.rootDisabled,
        !disabled &&
          !checked && [
            {
              selectors: {
                ":hover": styles.rootHovered,
                ":active": styles.rootPressed,
                [`${baseFabricFocusSelector}, ${baseFabricHoverFocusSelector}`]:
                  styles.rootFocused,
                [baseFabricHoverSelector]: { background: "inherit;" },
              },
            },
          ],
      ],
      splitMenu: [
        styles.root,
        {
          width: 32,
        },
        expanded && styles.rootExpanded,
        disabled && styles.rootDisabled,
        !disabled &&
          !expanded && [
            {
              selectors: {
                ":hover": styles.rootHovered,
                ":active": styles.rootPressed,
                [`${baseFabricFocusSelector}, ${baseFabricHoverFocusSelector}`]:
                  styles.rootFocused,
                [baseFabricHoverSelector]: { background: "inherit;" },
              },
            },
          ],
      ],
      linkContent: styles.linkContent,
      linkContentMenu: [
        styles.linkContent,
        {
          justifyContent: "center",
        },
      ],
      icon: [
        knownIcon && styles.iconColor,
        styles.icon,
        iconClassName,
        disabled && styles.iconDisabled,
      ],
      checkmarkIcon: [
        knownIcon && styles.checkmarkIcon,
        styles.icon,
        iconClassName,
      ],
      secondaryText: styles.secondaryText,
      subMenuIcon: [styles.subMenuIcon, subMenuClassName],
      label: [styles.label, disabled && styles.labelDisabled],
      labelDisabled: styles.labelDisabled,
      splitContainer: styles.splitButtonFlexContainer,
      screenReaderText: undefined,
    });
  }
);

export const getDividerClassNames = memoizeFunction(
  (theme: ITheme): IVerticalDividerClassNames => {
    const semanticColors = getStandardControlSemanticColorsWithTheme(theme);
    return mergeStyleSets({
      wrapper: [
        ribbonDividerWrapperClassName,
        {
          display: "inline-flex",
          height: "100%",
          alignItems: "center",
        },
      ],

      divider: mergeStyles(
        {
          width: 1,
          height: "100%",
          backgroundColor: isThemingEnabled()
            ? semanticColors.menuDividerBackgroundColor
            : "#c8c8c8",
        },
        scalePropertyWithDpi("width", (multiplier) => 1 * multiplier)
      ),
    });
  }
);

export const getOuterContainerClassName: () => string = memoizeFunction(() =>
  mergeStyles({
    display: "inline",
    width: "100%",
  })
);
